import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../../environments/environment';

import { AddressModel } from './model/address.model';
import { AddressResponse } from './response/address.response';
import { AddressResponseMessage } from './message/address-response.message';
import { ErrorResponseMessage } from '../../messages/error-response.message';
import { ResponseService } from '../response/response.service';

/**
 * Service to manage address request.
 */
@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private readonly addressCache = new Map<string, AddressModel[]>();

  constructor(private readonly httpClient: HttpClient, private readonly responses: ResponseService) {}

  /**
   * Returns cached addresses for the postcode if available.
   */
  getCachedAddresses(postcode: string): AddressModel[] | undefined {
    return this.addressCache.get(this.normalizePostcode(postcode));
  }

  /**
   * Finds address by postcode.
   */
  findAddress(postcode: string): Promise<AddressModel[]> {
    const normalizedPostcode = this.normalizePostcode(postcode);
    const cachedAddresses = this.addressCache.get(normalizedPostcode);

    if (cachedAddresses) {
      return Promise.resolve(cachedAddresses);
    }

    return new Promise<AddressModel[]>((resolve: AddressResponseMessage, reject: ErrorResponseMessage): void => {
      this.httpClient.get(`${environment.postcodes.apiPath}/postcodes/${normalizedPostcode}`).subscribe(
        (response: AddressResponse) => {
          if (response && response.code === 2000 && response.result) {
            const addressItems: AddressModel[] = this.responses.mapAddressItems(response.result);
            this.addressCache.set(normalizedPostcode, addressItems);
            resolve(addressItems);
          } else {
            resolve([]);
          }
        },
        (error: HttpErrorResponse) => reject(error)
      );
    });
  }

  private normalizePostcode(postcode: string): string {
    return (postcode ?? '').replace(/\s+/g, '').toUpperCase();
  }
}
